import { BookOpenText, CalendarDays, Hash, LibraryBig, Search, Sparkles } from 'lucide-react';
import type { FeatureContent } from './types';

/**
 * La ricerca nelle fonti dell'app (/search): la banca dati, senza domanda.
 * Tutto quello che si promette qui la pagina dell'app lo fa davvero — fonti
 * per categoria (i CCNL no: dalla ricerca sono esclusi finché il backend non
 * li supporta), estremi, articolo solo sulla Normativa Italiana, periodo,
 * scheda con la sintesi, nessun quesito consumato.
 */
export const ricercaNelleFonti: FeatureContent = {
  slug: 'ricerca-nelle-fonti',
  navLabel: 'Ricerca nelle fonti',
  menuDescription:
    'Cerca nelle fonti ufficiali per estremi, articolo o parole chiave e apri il documento con la sua sintesi.',
  cardDescription:
    'Cerca direttamente tra normativa, prassi e giurisprudenza, anche senza partire da un quesito, come in una banca dati professionale.',
  meta: {
    title: 'Ricerca nelle fonti ufficiali: normativa, prassi e giurisprudenza',
    description:
      'Cerca in normativa, prassi e giurisprudenza per estremi, articolo o parole chiave. Trovanorma apre il documento con la sua sintesi e il link all’originale, senza consumare quesiti.',
  },
  hero: {
    title: 'Ricerca nelle fonti',
    subtitle:
      'Cerca direttamente tra normativa, prassi e giurisprudenza. Trova norme, sentenze, circolari e provvedimenti per estremi, articolo o parole chiave, leggine la sintesi e consulta la fonte originale.',
  },
  statement: 'Quando sai già cosa cercare, vai dritto alla fonte',
  statementText:
    'Non sempre serve formulare un quesito. Scegli la fonte che vuoi consultare e cerca direttamente il documento che ti serve, per riferimento o per parole chiave. La ricerca nelle fonti non consuma i quesiti del tuo abbonamento.',
  blocks: [
    {
      icon: Search,
      kicker: 'Fonti ufficiali',
      title: 'Scegli dove cercare',
      description:
        'Normativa italiana ed europea, leggi regionali, prassi dell’Agenzia delle Entrate, INPS, INAIL e delle altre autorità, giurisprudenza di Cassazione, tributaria, amministrativa ed europea. Scegli la fonte e cerca direttamente al suo interno.',
    },
    {
      icon: Hash,
      kicker: 'Estremi e articoli',
      title: 'Arriva dritto all’atto o all’articolo',
      description:
        'Conosci già il riferimento? Cerca, ad esempio, D.Lgs. 24/2023, Cass. 24714/2026 o art. 2119 c.c. Se indichi un articolo, Trovanorma ti porta direttamente al punto che stai cercando.',
    },
    {
      icon: Sparkles,
      kicker: 'Parole chiave',
      title: 'Parti dal tema che ti interessa',
      description:
        'Non hai un riferimento preciso? Cerca per parole chiave. I risultati vengono ordinati per pertinenza e accompagnati da una sintesi, così puoi capire rapidamente quali documenti vale la pena approfondire.',
    },
    {
      icon: CalendarDays,
      kicker: 'Periodo',
      title: 'Gli orientamenti più recenti, quando servono',
      description:
        'Limita la ricerca agli ultimi 3 o 12 mesi quando vuoi concentrarti sugli orientamenti e sulla prassi più recenti, oppure estendi la ricerca a tutto l’archivio.',
    },
    {
      icon: BookOpenText,
      kicker: 'Sintesi del documento',
      title: 'Capisci subito cosa contiene',
      description:
        'Ogni risultato è accompagnato da una sintesi dei contenuti principali. Per le sentenze trovi anche gli elementi utili per orientarti nella pronuncia, come esito, parti, decisione, norme richiamate e massime. Quando vuoi verificare, puoi aprire il documento originale.',
    },
    {
      icon: LibraryBig,
      kicker: 'Senza consumare quesiti',
      title: 'Cerca liberamente, senza consumare quesiti',
      description:
        'Le ricerche nelle fonti sono comprese nell’abbonamento e non consumano quesiti. Cerca, apri i risultati e consulta le sintesi tutte le volte che ti serve, lasciando i quesiti alle attività che richiedono l’elaborazione di Trovanorma.',
    },
  ],
  steps: [
    {
      step: 1,
      title: 'Scegli dove cercare',
      description: 'Seleziona la fonte che vuoi consultare tra normativa, prassi e giurisprudenza.',
    },
    {
      step: 2,
      title: 'Cerca il documento',
      description:
        'Inserisci gli estremi di un atto, un articolo oppure le parole chiave del tema che stai cercando.',
    },
    {
      step: 3,
      title: 'Consulta e verifica',
      description:
        'Leggi la sintesi preparata da Trovanorma, consulta il documento e, quando vuoi, apri la fonte originale per verificarlo.',
    },
  ],
  relatedSolutions: ['avvocati', 'consulenti-del-lavoro'],
  closing: {
    title: 'La prossima fonte che cerchi, trovala direttamente in Trovanorma',
    text: 'Cerca una norma, una sentenza, una circolare o un provvedimento per riferimento o parole chiave. Leggi la sintesi, consulta il documento e verifica la fonte originale, senza consumare quesiti.',
  },
};
